// ME Campaigns — data access (S3 / D52 / D57). A campaign is a dial-config
// container that owns 0..1 drip sequence (via drip_campaigns.campaign_id) and is
// assigned raw leads (manual or auto-by-criteria). Reuses the live WS-2 drip engine
// + enroll RPC + runner UNCHANGED — this layer never touches the runner,
// me_call_queue, leads, or create_lead_intake.
import { getSupabaseClient } from "../providers/supabase/supabase";

export type Campaign = {
  id: string;
  name: string;
  status: string;
  audience: string;
  channel: string;                 // call | sms | email | mail (D74)
  max_sends_per_day: number | null; // advisory only (D74)
  dialing_mode: string;
  call_order: string;
  max_calls_per_day: number | null;
  max_attempts_per_record: number;
  retry_value: number;
  retry_unit: string;
  call_recording: boolean;
  suppress_federal_dnc: boolean;
  suppress_system_dnc: boolean;
  suppress_wireless: boolean;
  suppress_litigator: boolean;
  calling_hours_start: string;
  calling_hours_end: string;
  timezone: string;
  call_result_set: string[];
  agent_script_id: string | null;
  auto_assign_criteria: Record<string, string> | null;
  auto_assign_enabled: boolean;
  smart_local_presence: boolean;
};

export type DripStep = {
  id?: string;
  step_order: number;
  delay_value: number;
  delay_unit: string;
  action_type: string;
  body?: string | null;
  subject?: string | null;
  task_name?: string | null;
  mail_piece?: string | null;
};

const asArray = <T,>(v: T | T[] | null | undefined): T[] => (Array.isArray(v) ? v : v ? [v] : []);

// ---- list --------------------------------------------------------------------
export async function loadCampaigns() {
  const sb = getSupabaseClient();
  const [{ data: camps }, { data: rl }, { data: cu }] = await Promise.all([
    sb.from("campaigns").select("*").eq("audience", "raw_lead").order("created_at", { ascending: false }),
    sb.from("raw_leads").select("campaign_id").not("campaign_id", "is", null),
    sb.from("campaign_users").select("campaign_id,user_id"),
  ]);
  const leadCt: Record<string, number> = {};
  for (const r of (rl ?? []) as any[]) leadCt[r.campaign_id] = (leadCt[r.campaign_id] ?? 0) + 1;
  const agentCt: Record<string, number> = {};
  for (const r of (cu ?? []) as any[]) agentCt[r.campaign_id] = (agentCt[r.campaign_id] ?? 0) + 1;
  return ((camps ?? []) as Campaign[]).map((c) => ({
    ...c,
    total_leads: leadCt[c.id] ?? 0,
    agent_count: agentCt[c.id] ?? 0,
  }));
}

// ---- one campaign (full) -----------------------------------------------------
export async function loadCampaign(id: string) {
  const sb = getSupabaseClient();
  const [{ data: camp }, { data: agents }, { data: numbers }, { data: drip }] = await Promise.all([
    sb.from("campaigns").select("*").eq("id", id).maybeSingle(),
    sb.from("campaign_users").select("user_id").eq("campaign_id", id),
    sb.from("campaign_phone_numbers").select("phone_number_id").eq("campaign_id", id),
    sb.from("drip_campaigns").select("id,name,is_active").eq("campaign_id", id).limit(1),
  ]);
  const dripCampaign = asArray<any>(drip)[0] ?? null;
  let steps: DripStep[] = [];
  if (dripCampaign) {
    const { data: st } = await sb.from("drip_steps").select("*").eq("campaign_id", dripCampaign.id).order("step_order");
    steps = (st ?? []) as DripStep[];
  }
  return {
    campaign: camp as Campaign,
    agentIds: ((agents ?? []) as any[]).map((a) => a.user_id),
    numberIds: ((numbers ?? []) as any[]).map((n) => n.phone_number_id),
    dripCampaign,
    steps,
  };
}

// ---- create / update ---------------------------------------------------------
export async function saveCampaign(row: Partial<Campaign> & { id?: string }) {
  const sb = getSupabaseClient();
  if (row.id) return sb.from("campaigns").update(row).eq("id", row.id).select("id").single();
  return sb.from("campaigns").insert({ ...row, audience: "raw_lead" }).select("id").single();
}
export async function deleteCampaign(id: string) {
  return getSupabaseClient().from("campaigns").delete().eq("id", id);
}

// replace the campaign's agents / numbers (simple delete-then-insert)
export async function saveAgents(campaignId: string, userIds: string[]) {
  const sb = getSupabaseClient();
  await sb.from("campaign_users").delete().eq("campaign_id", campaignId);
  if (userIds.length) await sb.from("campaign_users").insert(userIds.map((user_id) => ({ campaign_id: campaignId, user_id })));
}
export async function saveNumbers(campaignId: string, phoneIds: string[]) {
  const sb = getSupabaseClient();
  await sb.from("campaign_phone_numbers").delete().eq("campaign_id", campaignId);
  if (phoneIds.length) await sb.from("campaign_phone_numbers").insert(phoneIds.map((phone_number_id) => ({ campaign_id: campaignId, phone_number_id })));
}

// attach/replace the drip sequence (reuses drip_campaigns/drip_steps + the live runner)
export async function saveDrip(campaignId: string, campaignName: string, steps: DripStep[]) {
  const sb = getSupabaseClient();
  let { data: drip } = await sb.from("drip_campaigns").select("id").eq("campaign_id", campaignId).limit(1).maybeSingle();
  let dripId = (drip as any)?.id as string | undefined;
  if (!dripId) {
    const { data } = await sb.from("drip_campaigns").insert({ name: `${campaignName} drip`, campaign_id: campaignId, is_active: true }).select("id").single();
    dripId = (data as any)?.id;
  }
  if (!dripId) return;
  await sb.from("drip_steps").delete().eq("campaign_id", dripId);
  if (steps.length) {
    await sb.from("drip_steps").insert(
      steps.map((s, i) => ({
        campaign_id: dripId, step_order: i + 1,
        delay_value: s.delay_value, delay_unit: s.delay_unit, action_type: s.action_type,
        body: s.body ?? null, subject: s.subject ?? null, task_name: s.task_name ?? null, mail_piece: s.mail_piece ?? null,
      })),
    );
  }
}

// ---- option loaders ----------------------------------------------------------
export async function loadOptions() {
  const sb = getSupabaseClient();
  const [users, phones, scripts, lists] = await Promise.all([
    sb.from("users").select("id,full_name,email,is_active").eq("is_active", true).order("full_name"),
    sb.from("phone_numbers").select("id,e164,purpose,is_active").order("e164"),
    sb.from("scripts").select("id,name").order("name"),
    sb.from("lists").select("id,title").order("title"),
  ]);
  return {
    users: (users.data ?? []) as any[],
    phones: (phones.data ?? []) as any[],
    scripts: (scripts.data ?? []) as any[],
    lists: (lists.data ?? []) as any[],
  };
}

// ---- assignment --------------------------------------------------------------
export async function assignByList(campaignId: string, listId: string) {
  const sb = getSupabaseClient();
  const { data } = await sb.from("raw_lead_lists").select("raw_lead_id").eq("list_id", listId);
  const ids = (data ?? []).map((r: any) => r.raw_lead_id);
  if (!ids.length) return { data: 0, error: null };
  return sb.rpc("assign_raw_leads_to_campaign", { p_campaign_id: campaignId, p_raw_lead_ids: ids });
}
export async function applyAutoAssign(campaignId: string) {
  return getSupabaseClient().rpc("apply_campaign_auto_assign", { p_campaign_id: campaignId });
}
export async function assignIds(campaignId: string, ids: string[]) {
  return getSupabaseClient().rpc("assign_raw_leads_to_campaign", { p_campaign_id: campaignId, p_raw_lead_ids: ids });
}

// ---- Phase 2 (D74): SMS/Email cohort enrollment + templates -------------------
// Templates for the message-sequence picker, filtered by the base's channel.
export async function loadTemplates(channel: string) {
  const { data } = await getSupabaseClient().from("templates").select("id,name,subject,body").eq("channel", channel).order("name");
  return (data ?? []) as { id: string; name: string; subject: string | null; body: string }[];
}

// The master campaigns this base is composed into (+ activation state + cohort size).
export async function loadBaseMasters(baseCampaignId: string) {
  const sb = getSupabaseClient();
  const { data: links } = await sb.from("master_campaign_bases").select("master_id").eq("base_campaign_id", baseCampaignId);
  const ids = ((links ?? []) as any[]).map((l) => l.master_id);
  if (!ids.length) return [] as { id: string; code: string | null; name: string; kind: string; activated: boolean; cohort: number }[];
  const [{ data: masters }, { data: rl }] = await Promise.all([
    sb.from("master_campaigns").select("id,code,name,kind,activated_at").in("id", ids),
    sb.from("raw_leads").select("master_campaign_id").in("master_campaign_id", ids),
  ]);
  const cohort: Record<string, number> = {};
  for (const r of (rl ?? []) as any[]) cohort[r.master_campaign_id] = (cohort[r.master_campaign_id] ?? 0) + 1;
  return ((masters ?? []) as any[]).map((m) => ({
    id: m.id, code: m.code, name: m.name, kind: m.kind,
    activated: m.activated_at != null, cohort: cohort[m.id] ?? 0,
  }));
}

// Enroll a master's cohort into this (sms/email) base's sequence. Returns the count enrolled.
export async function enrollCohort(masterId: string, baseCampaignId: string): Promise<number> {
  const { data, error } = await getSupabaseClient().rpc("enroll_master_cohort_in_base", {
    p_master_id: masterId, p_base_campaign_id: baseCampaignId,
  });
  if (error) throw new Error(error.message);
  return (data as number) ?? 0;
}

// ---- clone / recycle (CC-S1 / D67) -------------------------------------------
// Deep-copy config + agents + numbers + linked drip (NOT lead assignments).
// Returns the new draft campaign's id.
export async function cloneCampaign(sourceId: string): Promise<string> {
  const { data, error } = await getSupabaseClient().rpc("clone_campaign", { p_campaign_id: sourceId });
  if (error) throw new Error(error.message);
  return data as string;
}
// Clone a source, rename, and MOVE its non-connected raw leads to the new draft.
export async function recycleCampaign(sourceId: string, newName: string): Promise<string> {
  const { data, error } = await getSupabaseClient().rpc("recycle_campaign", {
    p_source_campaign_id: sourceId,
    p_new_name: newName,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

// Recycle preview: how many of a source campaign's leads are recyclable (non-connected)
// vs already connected — so the editor can show "N leads will carry over" before running.
export async function recyclePreview(sourceId: string): Promise<{ total: number; connected: number; recyclable: number }> {
  const sb = getSupabaseClient();
  const { data } = await sb
    .from("raw_leads")
    .select("id,source_lead_id")
    .eq("campaign_id", sourceId)
    .is("duplicate_of", null);
  const rows = (data ?? []) as { id: string; source_lead_id: string | null }[];
  const leadIds = rows.map((r) => r.source_lead_id).filter(Boolean) as string[];
  let connectedLeadIds = new Set<string>();
  if (leadIds.length) {
    const { data: acts } = await sb
      .from("activities")
      .select("lead_id")
      .eq("type", "call")
      .eq("disposition", "connected")
      .in("lead_id", leadIds);
    connectedLeadIds = new Set(((acts ?? []) as { lead_id: string }[]).map((a) => a.lead_id));
  }
  const connected = rows.filter((r) => r.source_lead_id && connectedLeadIds.has(r.source_lead_id)).length;
  return { total: rows.length, connected, recyclable: rows.length - connected };
}

// ---- Buy a new Twilio number (reuses the twilio-numbers edge fn; PhonePage parity) ----
async function twilioNumbers(payload: Record<string, unknown>) {
  const { data: sess } = await getSupabaseClient().auth.getSession();
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twilio-numbers`, {
    method: "POST",
    headers: {
      apikey: import.meta.env.VITE_SB_PUBLISHABLE_KEY as string,
      Authorization: `Bearer ${sess.session?.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((d as any)?.error || `failed (${res.status})`);
  return d as any;
}
export async function searchNumbers(areaCode: string, contains: string): Promise<{ phoneNumber: string; friendlyName?: string }[]> {
  const d = await twilioNumbers({ action: "search", areaCode, contains });
  return (d?.numbers ?? []) as { phoneNumber: string; friendlyName?: string }[];
}
// Buy a number, then return the newly-provisioned phone_numbers row id so the editor can attach it.
export async function buyNumber(phoneNumber: string): Promise<string | null> {
  await twilioNumbers({ action: "buy", phoneNumber });
  const { data } = await getSupabaseClient().from("phone_numbers").select("id").eq("e164", phoneNumber).maybeSingle();
  return ((data as any)?.id as string) ?? null;
}
